import  React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";

import Close from "@mui/icons-material/Close";
import Send from "@mui/icons-material/Send";
import Science from "@mui/icons-material/Science";
import moment from "moment";
import { formatWithOutSom } from "../Message";

import axios from "axios";
import { loadLabdb, requestLab ,loadRequestedLabsInpatients } from "../BaseURL";
import Swal from "sweetalert2";

const RequestLabDialog = ({ open, onClose, patient, sendstartDate }) => {

  const currentDate = moment().format("YYYY-MM-DD");

  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [requestedLabs, setRequestedLabs] = useState([]);

  const [requestDate, setRequestDate] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // 🔥 Track which lab is being requested by unique identifier
  const [requestingLabId, setRequestingLabId] = useState(null);

  useEffect(() => {
    if (open && patient?.id) {
      setRequestDate(currentDate); 
      loadLabs();
      loadRequestedLabs()
    }
  }, [open, patient]);

  const loadLabs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(loadLabdb, {
        params: { insurance: patient.insurance },
      });

      if (res.data.success) {
        setLabs(res.data.results || []);
      }
    } catch (error) {
      console.error("Error loading labs:", error);
    } finally {
      setLoading(false);
    }
  };

 
  const handleRequestLab = async (lab, labId) => {
    // Validation rules
    if (!requestDate) {
      setSnackbarMessage("❌ Please select a request date");
      setSnackbarOpen(true);
      return;
    }

    if (moment(requestDate).isBefore(moment(sendstartDate))) {
      setSnackbarMessage(
        `❌ Date cannot be earlier than admission date (${sendstartDate})`
      );
      setSnackbarOpen(true);
      return;
    }

    if (moment(requestDate).isAfter(moment(currentDate))) {
      setSnackbarMessage(`❌ Date cannot be in the future`);
      setSnackbarOpen(true);
      return;
    }

      const alreadyRequested = requestedLabs.some(
        (r) => r.lab === lab.name && r.date === currentDate
      );
    
      if (alreadyRequested) {
        const result = await Swal.fire({
          title: "Duplicate Request",
          text: `${lab.name} has already been requested today. Do you want to request again?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, Request Again",
          cancelButtonText: "No"
        });
    
        if (!result.isConfirmed) {
          return; // user cancelled
        }
      }

    // 🔥 START loading only for THIS lab using unique ID
    setRequestingLabId(labId);

    try {
      const res = await axios.post(requestLab, {
        patientId: patient.id,
        dat: requestDate,
        patientInsurance: patient.insurance,
        labName: lab.name,
        amount: lab.amount,
        base_cost: lab.base_cost,
        code: lab.code,
        currentUser: patient.currentUser,
        type: "Lab",
      });

      // 🔥🔥 HANDLE BOTH SUCCESS & FAILURE 🔥🔥
      if (!res.data.success) {
        setSnackbarMessage(`❌ ${res.data.message}`);
      } else {
        setSnackbarMessage(`✅ ${lab.name} requested successfully`);
        loadRequestedLabs()
      }
      
      setSnackbarOpen(true);

    } catch (error) {
      
      setSnackbarMessage(`❌ Failed to request ${lab.name}`);
      setSnackbarOpen(true);
    } finally {
      // 🟢 Always clear the loading state
      setRequestingLabId(null);
    }
  };


  const loadRequestedLabs = async () => {
    if (!patient || !patient.id) return;
  
    setLoading(true);
    try {
      const response = await axios.get(loadRequestedLabsInpatients, {
        params: {
          patientId: patient.id,
          dat:sendstartDate ,
          currentDate
        },
      });
  
      if (response.data.success) {
        setRequestedLabs(response.data.results);
      }
  
    } catch (err) {
      console.error("Failed to load requested labs:", err);
    }finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Science />
            Request Lab
          </span>

          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={12} md={8} size={6}>
              <TextField
                fullWidth
                placeholder="Search lab..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ mt: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={4} size={6}>
              <TextField
                fullWidth
                label="Request Date"
                type="date"
                value={requestDate}
                onChange={(e) => setRequestDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>

          {loading && (
            <Typography sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Typography>
          )}

          {!loading && (
            <Paper sx={{ p: 1, mt: 2 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Lab</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">
                        Request
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {labs
                      .filter((l) =>
                        l.name.toLowerCase().includes(search.toLowerCase())
                      )
                      .map((lab, idx) => {
                        // 🔥 Create unique identifier for each lab
                        const labId = `${lab.code}-${idx}`;
                        
                        return (
                          <TableRow key={labId}>
                            <TableCell>{lab.name}</TableCell>
                            <TableCell>{formatWithOutSom(lab.amount)}</TableCell>

                            <TableCell align="center">
                              {requestingLabId === labId ? (
                                <CircularProgress size={24} />
                              ) : (
                                <IconButton
                                  color="primary"
                                  onClick={() => handleRequestLab(lab, labId)}
                                >
                                  <Send />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={onClose}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbarMessage.includes("❌") ? "error" : "success"}
          sx={{ fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequestLabDialog;